import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import apiClient from '../services/apiClient';

// Every consumer (login overlay, Dashboard banner) and every remount shares
// one request per user: without this each instance fetched on its own —
// doubled again by StrictMode — and the banner refetched on every visit to
// the Dashboard. A short TTL still picks up notifications an admin publishes
// or ends mid-session.
const CACHE_TTL_MS = 60 * 1000;
let cache = null; // { userId, fetchedAt, promise }

const fetchPending = (userId) => {
  if (cache && cache.userId === userId && Date.now() - cache.fetchedAt < CACHE_TTL_MS) {
    return cache.promise;
  }
  const promise = apiClient.get('/api/notifications/pending')
    .then((res) => res.data.notifications || [])
    .catch((err) => {
      if (cache && cache.promise === promise) cache = null; // let the next mount retry
      throw err;
    });
  cache = { userId, fetchedAt: Date.now(), promise };
  return promise;
};

// Live admin-managed announcements (see backend api/v1/notifications.py) for
// the current user. The backend decides what is live and only returns
// anything to paying (Stripe) subscribers; admins and impersonation sessions
// are skipped client-side too.
const useLiveNotifications = () => {
  const { user, isAdmin, isImpersonating, hasActiveSubscription } = useAuth();
  const [notifications, setNotifications] = useState([]);

  const eligible = !!user && !isAdmin && !isImpersonating && hasActiveSubscription;

  useEffect(() => {
    if (!eligible) {
      setNotifications([]);
      return undefined;
    }
    let cancelled = false;
    fetchPending(user.id)
      .then((items) => {
        if (!cancelled) setNotifications(items);
      })
      .catch((err) => console.error('[useLiveNotifications] Error fetching notifications:', err));
    return () => { cancelled = true; };
  }, [eligible, user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  return { eligible, notifications };
};

export default useLiveNotifications;
